import { withDependencies } from '@wix/thunderbolt-ioc'
import type {
	BrowserWindow,
	Experiments,
	IAppDidMountHandler,
	IAppWillUnmountHandler,
	ViewerModel,
} from '@wix/thunderbolt-symbols'
import { BrowserWindowSymbol, ExperimentsSymbol, ViewerModelSym } from '@wix/thunderbolt-symbols'
import { isSSR } from '@wix/thunderbolt-commons'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'
import { OnLinkClickSymbol } from './symbols'
import { PREVENT_ANCHOR_RELOAD_SPEC } from './preHydrationAnchorGuard'
import type { PreHydrationGuard } from './preHydrationAnchorGuard'
import type { IOnLinkClickHandler } from './types'
import React from 'react'

const SKIP_HYDRATION_SPEC = 'specs.thunderbolt.skip_hydration'

export const clickHandlerRegistrarFactory = (
	browserWindow: NonNullable<BrowserWindow>,
	{ onLinkClick }: IOnLinkClickHandler,
	viewerModel: ViewerModel,
	experiments: Experiments
): IAppDidMountHandler & IAppWillUnmountHandler => {
	// The guard is registered on `document` in capture phase
	const removeGuard = () => {
		if (isExperimentOpen(experiments, PREVENT_ANCHOR_RELOAD_SPEC) && browserWindow.__tbAnchorGuard) {
			browserWindow.document.removeEventListener('click', browserWindow.__tbAnchorGuard, true)
			browserWindow.__tbAnchorGuard = undefined
		}
	}

	return {
		appDidMount: () => {
			if (isSSR(browserWindow)) {
				return
			}
			browserWindow.addEventListener('click', onLinkClick)
			if (isExperimentOpen(experiments, SKIP_HYDRATION_SPEC)) {
				// Nothing will hydrate, so the guard would block these links forever
				removeGuard()
			} else if (isExperimentOpen(experiments, PREVENT_ANCHOR_RELOAD_SPEC) && browserWindow.__tbAnchorGuard) {
				// Keep the guard: React still swallows clicks on subtrees it hasn't hydrated yet (TB-15978).
				// The guard lets each anchor through once it is hydrated and this flag is set.
				;(browserWindow.__tbAnchorGuard as PreHydrationGuard).linkClickReady = true
			}
			if (viewerModel.mode.debug && React.version.startsWith('18')) {
				// for some reason react 18 strict mode doesn't fire click events on window
				Array.from(document.querySelectorAll?.('a') || []).map((e) => e.addEventListener('click', onLinkClick))
			}
		},
		appWillUnmount: () => {
			browserWindow.removeEventListener('click', onLinkClick)
			removeGuard()
		},
	}
}

export const ClickHandlerRegistrar = withDependencies(
	[BrowserWindowSymbol, OnLinkClickSymbol, ViewerModelSym, ExperimentsSymbol],
	clickHandlerRegistrarFactory
)
